/**
 * quarantine -- how the UI names and describes the quarantine holding area (2026-10-19, OP#1932).
 *
 * THE BUG THIS EXISTS FOR. The quarantine org (org 1, seeded at install as "Default
 * Organization", `is_quarantine=true`) is where devices land when they connect to Meridian before
 * anyone assigns them to a customer. With it selected the Overview read
 *
 *     "Overview -- Every site for Default Organization -- 1 site · 0 devices · 0 offline"
 *
 * i.e. the holding area looked like a customer called "Default Organization" with one site (the
 * bootstrap placeholder "Default Site", which services/tenancy.ensure_default_tenant re-creates
 * on every start). Nothing said what the page was for or what to do with a device found there.
 *
 * THE RULE, in one place so every surface (ScopeBar, pickers, Overview, Customers, Sites,
 * Health, Alerts, command palette, Onboard) says the same thing:
 *   - a quarantine org is labelled QUARANTINE_LABEL, never by its stored name;
 *   - its sites are placeholders, NOT customer sites: they are excluded from any "N sites"
 *     count and named as placeholders where they appear (excluding is clearer than flagging
 *     "unexpected": the seeded one is expected and cannot be removed -- startup re-creates it);
 *   - the Overview is replaced by a panel of devices waiting to be claimed.
 *
 * Pure (no React, no store, no "@/" runtime imports) so `npm run test:unit` can pin it directly
 * (quarantine.test.ts). Type-only imports are erased by Node's type stripping.
 */
import type { Device, Org, Site } from "@/types/domain";

/** What every surface calls the holding area instead of its stored org name. */
export const QUARANTINE_LABEL = "Quarantine (unclaimed devices)";

/** The one-line explanation at the top of the Quarantine panel (owner wording, 2026-10-19). */
export const QUARANTINE_EXPLAINER =
  "Devices that connect to Meridian before they are assigned to a customer land here. " +
  "Assign each one to a site to put it into service.";

/** Empty state: nothing in the holding area. */
export const QUARANTINE_EMPTY = "No devices waiting to be claimed.";

/** Shown next to every Assign action: the claim can kick off an upgrade (devices.py
 *  assign_devices_to_site -> onboard_template.maybe_firmware_update). */
export const QUARANTINE_FIRMWARE_NOTE =
  "Assigning can start a firmware upgrade when the target customer has a firmware policy.";

/** Shown instead of the Assign action to anyone below platform_admin. The server agrees:
 *  POST /devices/assign skips a quarantine-pool device for every caller below platform_admin. */
export const QUARANTINE_ROLE_NOTE = "Only a platform admin can release a device from quarantine.";

/** The minimum an org-shaped value needs for labelling (Org from /me, Customer from /customers). */
export interface OrgLike {
  name: string;
  is_quarantine?: boolean | null;
}

/** True only for an org explicitly flagged quarantine. A missing flag (older controller) is NOT
 *  quarantine, so an unknown never relabels a real customer. */
export function isQuarantineOrg(org: OrgLike | null | undefined): boolean {
  return org?.is_quarantine === true;
}

/** The name to SHOW for an org: QUARANTINE_LABEL for the holding area, else its own name.
 *  null in -> null out, so callers keep their existing "no customer selected" fallbacks. */
export function orgLabel(org: OrgLike | null | undefined): string | null {
  if (!org) return null;
  return isQuarantineOrg(org) ? QUARANTINE_LABEL : org.name;
}

/** "3 devices waiting to be claimed" / "1 device waiting to be claimed" / the empty line. */
export function waitingSummary(n: number): string {
  if (n <= 0) return QUARANTINE_EMPTY;
  return `${n} ${n === 1 ? "device" : "devices"} waiting to be claimed`;
}

/** The fields the waiting list reads; a structural subset of Device so tests stay small. */
export type WaitingDevice = Pick<Device, "serial" | "model" | "last_seen" | "registered_at">;

/**
 * Order of the waiting list: most recent LAST CONTACT first (a unit that is plugged in right now
 * is the one an installer is standing next to), never-contacted devices last, then serial so the
 * order is stable between refreshes.
 */
export function sortWaiting<T extends WaitingDevice>(devices: T[]): T[] {
  const t = (iso: string | null | undefined) => {
    if (!iso) return -Infinity;
    const zoned = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`;
    const v = new Date(zoned).getTime();
    return Number.isNaN(v) ? -Infinity : v;
  };
  return devices
    .slice()
    .sort((a, b) => t(b.last_seen) - t(a.last_seen) || a.serial.localeCompare(b.serial));
}

/** One customer and the sites a device may be assigned to. */
export interface AssignTargetGroup {
  orgId: string;
  orgName: string;
  sites: Array<Pick<Site, "id" | "name">>;
}

/**
 * Where a quarantined device may go: every NON-quarantine org's sites, grouped by customer, both
 * levels sorted by name. Quarantine placeholders are never a target (assigning a device to the
 * holding area's own placeholder site would not put it into service). Orgs with no site are
 * dropped: there is nothing to pick.
 */
export function assignTargets(
  orgs: Array<Pick<Org, "id" | "name" | "is_quarantine">>,
  sites: Array<Pick<Site, "id" | "org_id" | "name">>,
): AssignTargetGroup[] {
  return orgs
    .filter((o) => !isQuarantineOrg(o))
    .map((o) => ({
      orgId: o.id,
      orgName: o.name,
      sites: sites
        .filter((s) => s.org_id === o.id)
        .map((s) => ({ id: s.id, name: s.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .filter((g) => g.sites.length > 0)
    .sort((a, b) => a.orgName.localeCompare(b.orgName));
}

/**
 * The footnote naming the holding area's placeholder sites, or null when it has none. Excluded
 * from every count; this line says so instead of letting "1 site" stand unexplained.
 */
export function placeholderSitesNote(sites: Array<Pick<Site, "name">>): string | null {
  if (sites.length === 0) return null;
  const names = sites.map((s) => s.name).join(", ");
  return sites.length === 1
    ? `${names} is a placeholder created at install, not a customer site, and is not counted.`
    : `${names} are placeholders, not customer sites, and are not counted.`;
}

/** Result line after POST /devices/assign. `skipped` means the server refused or found nothing
 *  to move (out of reach, already there, or not platform_admin) -- said plainly, never hidden. */
export function assignResultMessage(
  r: { assigned: number; skipped: number; firmware_queued?: number },
  siteName: string,
): string {
  if (r.assigned === 0) {
    return `Not assigned: the controller skipped the device (${r.skipped} skipped).`;
  }
  const fw = r.firmware_queued ? " A firmware upgrade was queued." : "";
  return `Assigned to ${siteName}.${fw}`;
}
